import { useEffect, useReducer } from "react";

import { Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { Link, useLocation, useNavigate } from "react-router";

import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { Container } from "@/components/pieces/bootstrap";
import { LearnQuickViewLink } from "@/components/pieces/learn/LearnQuickViewLink";
import { CTA_COLOR, STANDARD_NAV_HEIGHT, SUBTEXT_COLOR } from "@/css/constants";

import BreakEvenChart from "./components/BreakEvenChart";
import CalculatorInputs from "./components/CalculatorInputs";
import CalculatorResults from "./components/CalculatorResults";
import MonthlyEarningsTable from "./components/MonthlyEarningsTable";
import PerformanceSimulator from "./components/PerformanceSimulator";
import ShareButton from "./components/ShareButton";
import {
  calculatorReducer,
  initialCalculatorState,
} from "./utils/calculatorReducer";
import { useBreakEvenAnalysis, useMonthlyEarnings } from "./utils/hooks";
import { deserializeParamsToState } from "./utils/shareUtils";

export function SalesCompCalculator() {
  const [state, dispatch] = useReducer(
    calculatorReducer,
    initialCalculatorState,
  );

  // Destructure the state for easier access
  const {
    baseSalary,
    targetCommission,
    commissionRate,
    rampMonths1to3,
    rampMonths4to6,
    rampMonths7to9,
    rampMonths10to12,
    guaranteeMonths1to3,
    guaranteeMonths4to6,
    accelerators,
    monthlyPerformances,
    ote,
    monthlyRampedQuotas,
  } = state;

  // Annual quota is derived from target commission and commission rate
  const annualQuota =
    commissionRate > 0
      ? Math.round(targetCommission / (commissionRate / 100))
      : 0;

  // Get location from react-router instead of window
  const location = useLocation();
  const navigate = useNavigate();

  // Load calculator state from URL parameters on mount
  useEffect(() => {
    const queryString = location.search;

    if (queryString) {
      const stateFromParams = deserializeParamsToState(queryString);

      if (Object.keys(stateFromParams).length > 0) {
        dispatch({
          type: "LOAD_STATE_FROM_URL",
          payload: stateFromParams,
        });

        // Use react-router's navigate to clean URL instead of direct window manipulation
        navigate(location.pathname, { replace: true });
      }
    }
  }, [location.search, navigate, location.pathname]);

  const monthlyEarnings = useMonthlyEarnings(
    annualQuota,
    commissionRate,
    baseSalary,
    targetCommission,
    accelerators,
    monthlyRampedQuotas,
    guaranteeMonths1to3,
    guaranteeMonths4to6,
    rampMonths1to3,
    rampMonths4to6,
    rampMonths7to9,
    rampMonths10to12,
    monthlyPerformances,
  );

  const { cumulativeData, breakEvenMonth } =
    useBreakEvenAnalysis(monthlyEarnings);

  return (
    <CalcContainer>
      <Link to="/ai-roleplays" style={{ color: "#FFF" }}>
        <Box backgroundColor={CTA_COLOR} py={3} color="#FFF">
          <Box px={24}>
            <Flex alignItems="center" justifyContent="space-between">
              <Box>
                <b>🚀 Just Launched:</b> Voice-based, AI Roleplays
              </Box>
              <Media greaterThan="sm">
                <Box fontWeight={600}>
                  Learn More <Icon icon="arrow-right" />
                </Box>
              </Media>
            </Flex>
          </Box>
        </Box>
      </Link>
      <Box bg="#FFF">
        <Flex
          justifyContent="space-between"
          alignItems={["flex-start", "flex-start", "center"]}
          flexDirection={["column", "column", "row"]}
          width="100%"
          mb={4}
          className="bbs"
          px={24}
          pt={36}
          pb={24}
        >
          <Box mb={["24px", "24px", "0", "0"]}>
            <h1>Sales Compensation Simulator</h1>
            <Box color={SUBTEXT_COLOR} mt={2}>
              Experiment with different sales compensation scenarios to see how
              they impact earnings.{" "}
              <LearnQuickViewLink link="/learn/sales-compensation-simulator">
                Read instructions here.
              </LearnQuickViewLink>
            </Box>
          </Box>
          <ShareButton
            calculatorState={{
              baseSalary,
              targetCommission,
              commissionRate,
              rampMonths1to3,
              rampMonths4to6,
              rampMonths7to9,
              rampMonths10to12,
              guaranteeMonths1to3,
              guaranteeMonths4to6,
              accelerators,
              monthlyPerformances,
            }}
          />
        </Flex>

        <Container maxWidth="100%" px={20}>
          <Flex
            css={{ gap: "48px" }}
            flexDirection={["column", "column", "column", "row"]}
            width="100%"
          >
            {/* Left Panel - Inputs */}
            <Flex flex={1}>
              <CalculatorInputs state={state} dispatch={dispatch} />
            </Flex>

            {/* Right Panel - Results */}
            <Box flex={2}>
              <CalculatorResults
                ote={ote}
                commissionRate={commissionRate}
                annualQuota={annualQuota}
                guaranteeMonths1to3={guaranteeMonths1to3}
                guaranteeMonths4to6={guaranteeMonths4to6}
                rampMonths1to3={rampMonths1to3}
                rampMonths4to6={rampMonths4to6}
                rampMonths7to9={rampMonths7to9}
                rampMonths10to12={rampMonths10to12}
                accelerators={accelerators}
              />

              {/* Performance Simulation section */}
              <PerformanceSimulator
                monthlyPerformances={monthlyPerformances}
                setMonthlyPerformances={(value) =>
                  dispatch({ type: "SET_MONTHLY_PERFORMANCES", payload: value })
                }
              />

              {/* Monthly Earnings Table */}
              <MonthlyEarningsTable
                monthlyEarnings={monthlyEarnings}
                baseSalary={baseSalary}
                annualQuota={annualQuota}
                monthlyRampedQuotas={monthlyRampedQuotas}
                commissionRate={commissionRate}
                accelerators={accelerators}
              />
            </Box>
          </Flex>
        </Container>

        {/* Break-even Analysis */}
        <BreakEvenChart
          breakEvenMonth={breakEvenMonth}
          cumulativeData={cumulativeData}
        />
      </Box>
    </CalcContainer>
  );
}

const CalcContainer = styled.div`
  margin-top: ${STANDARD_NAV_HEIGHT}px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  > div {
    width: 100%;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: "Inter", sans-serif !important;
  }

  h1 {
    font-size: 36px !important;
    margin: 0;
  }

  h2 {
    font-size: 24px !important;
  }

  h3 {
    font-size: 20px !important;
  }

  h4 {
    font-size: 18px !important;
  }

  h1:first-of-type,
  h2:first-of-type,
  h3:first-of-type,
  h4:first-of-type,
  h5:first-of-type,
  h6:first-of-type {
    margin-top: 0 !important;
  }

  td {
    padding: 10px 8px !important;
  }
  th {
    padding: 4px 8px !important;
  }

  .grid {
    width: 100%;
  }
`;
